import {
  useRef,
  useState,
  useEffect
} from 'react';
import { format } from 'date-fns';
import Image from '@atoms/Image';
import * as yup from 'yup';
import db from '@services/firebase-service';
import { yupValidator } from '@lib/yup-validator';
import {
  query,
  addDoc,
  orderBy,
  onSnapshot,
  collection,
  limitToLast
} from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import { pushDataLayerForEvent } from '@lib/gtag';
import { useHotspot } from '@lib/hooks/useHotSpot';
import { Pulse } from '@atoms/Pulse';
import Input from '@atoms/new/input';
import ScrollableFeed from 'react-scrollable-feed';

const ChatBox = ({ streamId, featureId, interactionType, showInputPulse }) => {
  const { isHotspotEnabled, handleHotspotClose } = useHotspot('sandbox:chat-input:hotspot');

  const [messages, setMessages] = useState([]);
  const userName = 'You';
  const activeUserId = 'user';
  const featureType = useRef(null);

  useEffect(() => {
    featureType.current = interactionType;
  }, [interactionType]);

  // eslint-disable-next-line camelcase
  const onComment = async ({ chat_input }) => {
    const rawMessagesRef = collection(db, 'streams', streamId, 'rawMessages');

    const document = {
      upvotes: 0,
      // eslint-disable-next-line camelcase
      message: chat_input,
      time: Date.now(),
      userId: activeUserId,
      name: userName,
      interaction: interactionType
    };

    setMessages((prev) => [...prev, document]);

    await addDoc(rawMessagesRef, document);
  };

  useEffect(() => {
    if (!streamId) return undefined;

    const unSubMessages = onSnapshot(
      query(
        collection(db, 'streams', streamId, 'processedMessages'),
        orderBy('time'),
        limitToLast(20)
      ),
      (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const document = {
              ...change.doc.data(),
              id: change.doc.id
            };

            if (
              document?.name === userName
              && document?.userId === activeUserId
            ) { return; }

            if (featureType.current !== document?.interaction) return;

            setMessages((prev) => [...prev, document]);
          }
        });
      }
    );

    return () => {
      unSubMessages();
    };
  }, [streamId]);

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    watch
  } = useForm({
    mode: 'onChange',
    resolver: yupValidator(
      yup.object().shape({
        chat_input: yup.string().required()
      })
    )
  });

  useEffect(() => {
    setFocus('chat_input');
  }, []);

  const handleComment = (data) => {
    pushDataLayerForEvent('sandbox_zoom_chatbox_send_message', { streamId }, false);
    onComment(data);
    reset({ chat_input: '' });
    setFocus('chat_input');
  };

  useEffect(() => {
    setMessages([]);
  }, [featureId]);

  const changeDetectionFilter = (previousProps, newProps) => {
    const prevChildren = previousProps.children;
    const newChildren = newProps.children;

    return prevChildren !== newChildren
      && prevChildren[prevChildren.length - 1] !== newChildren[newChildren.length - 1];
  };

  const input = watch('chat_input');

  return (<div className='w-full flex flex-col text-grey-900 '>
    <div className='p-2'>
      <div className="flex justify-between items-center">
        <Image height='6' width='10' src="/images/newUpArrow.svg" alt={'down-arrow'} />

        <p style={{
          fontSize: '12px',
          color: '#4A4A4A'
        }} className=" font-medium">Meeting Chat</p>
        <div />
      </div>
    </div>

    <ScrollableFeed
      className='p-4 flex-1'
      changeDetectionFilter={changeDetectionFilter}
    >
      {messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}
    </ScrollableFeed>

    <div>
      <div style={{
        background: '#F1F3F4'
      }} className='flex justify-center items-center'>
        <Image width="12" height="10" src="/images/sandbox/zoom-chatbox-footer-icon-1.svg" alt={'zoom-chatbox-footer-icon-1'} />
        <p style={{
          fontSize: '8px',
          color: '#6F6A6A',
          lineHeight: '12px'
        }} className="font-medium"> Who can see your messages?</p>
      </div>

      <form
        onSubmit={handleSubmit(handleComment)}
        className='p-2'>
        <p style={{
          fontSize: '10px',
          color: '#232333'
        }} className='font-semibold'>To: <span className='bg-blue-500 text-white rounded px-1'>Everyone</span></p>

        <div className='relative'>
          <Input
            autoFocus
            size='sm'
            value={input}
            autoComplete='off'
            {...register('chat_input')}
            style={{ fontSize: '10px' }}
            className="w-full h-6 font-normal md:leading-5 text-grey-900 placeholder:text-grey-900"
            placeholder='Type message here...'
          />
          {showInputPulse && isHotspotEnabled && (
            <div className='absolute -top-3 left-32'>
              <Pulse
                isEnabled={isHotspotEnabled}
                onClose={handleHotspotClose}
                tooltip='Please type your response here; it will reflect in the interaction.'
              />
            </div>
          )}
        </div>

        <input type="submit" hidden />

        <div className='flex items-center justify-between p-2'>
          <Image className="w-1/2 cursor-not-allowed" src="/images/sandbox/sandbox-zoom-chatbox-footer-icons.png" alt={'zoom-chatbox-footer-icons'} />

          <button type='submit'>
            <Image
              alt={'zoom-send-icon'}
              className='cursor-pointer'
              src="/images/sandbox/zoom-send-icon.svg"
            />
          </button>
        </div>
      </form>
    </div>
  </div>
  );
};

const Message = ({ message }) => (
  <div>
    <div
      style={{
        fontSize: '8px',
        lineHeight: '12px'
      }}
      className='flex font-medium ml-8 dark:text-grey-900 text-grey-900' >
      <p>{`${message.name} to Everyone `}</p>
      <p className='ml-4'>
        {format(new Date(message.time), 'hh:mm a')}
      </p>
    </div>

    <div className='mb-2 flex'>
      <div className='bg-lightBlue-700 mr-2 text-white w-6 h-6 text-center rounded-lg'>
        {message.name?.at(0)}
      </div>

      <div key={message.id} className='bg-lightBlue-100 rounded-lg p-2 w-full'>
        <p
          style={{
            fontSize: '14px',
            lineHeight: '18px',
            wordWrap: 'anywhere'
          }}
          className='font-normal'>
          {message.message}
        </p>
      </div>
    </div>
  </div>
);

export default ChatBox;
